// Fuseau horaire de Mayotte (UTC+3)
export const TZ = 'Indian/Mayotte';

/**
 * Formate une date en francais avec le fuseau de Mayotte.
 * @param {string|Date} d - date ISO ou objet Date
 * @param {object} opts - options Intl.DateTimeFormat (sans timeZone)
 * @returns {string}
 */
export function fmtDateTZ(d, opts = {}) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { timeZone: TZ, ...opts });
}

/**
 * Formate une heure en francais avec le fuseau de Mayotte.
 */
export function fmtTimeTZ(d, opts = {}) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('fr-FR', { timeZone: TZ, ...opts });
}

/**
 * Formate date + heure courte (jour mois, hh:mm)
 */
export function fmtDateTimeTZ(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Retourne la date du jour a Mayotte au format YYYY-MM-DD
 */
export function todayMayotte() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
  return parts; // en-CA gives YYYY-MM-DD
}

// ── Statut ouverture restaurant ───────────────────────────────────────────────
// Même règle que le serveur (isOpenSql, fonction restaurants) : planning
// hebdomadaire, sinon créneaux (time_slots), sinon horaires simples.

const DAYS_FR = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];

// Date « murale » de Mayotte (UTC+3), décalée de `dayOffset` jours : ses
// getters locaux (getDay, getDate, getHours…) donnent l'heure de Mayotte,
// quel que soit le fuseau du navigateur.
export function mayotteDate(dayOffset = 0) {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 3 * 3600000 + dayOffset * 86400000);
}

function mayotteNow() {
  const d = mayotteDate();
  return { dayFr: DAYS_FR[d.getDay()], minutes: d.getHours() * 60 + d.getMinutes() };
}

function toMin(t) {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return h * 60 + m;
}

// Bornes incluses ; un créneau qui passe minuit (22:00 – 02:00) est géré.
function inSlot(minutes, open, close) {
  const o = toMin(open);
  const c = toMin(close);
  return o <= c ? minutes >= o && minutes <= c : minutes >= o || minutes <= c;
}

const hhmm = (t) => (t || '').substring(0, 5);

/**
 * Créneaux réellement appliqués le jour J+dayOffset (à Mayotte) :
 * [{ open, close }], [] si fermé ce jour-là, null si aucun horaire renseigné.
 */
export function slotsForDay(restaurant, dayOffset = 0) {
  if (!restaurant) return null;
  const ws = restaurant.weekly_schedule;
  if (ws) {
    const s = ws[DAYS_FR[mayotteDate(dayOffset).getDay()]];
    if (!s || s.closed || !s.open || !s.close) return [];
    const slots = [{ open: hhmm(s.open), close: hhmm(s.close) }];
    if (s.open2 && s.close2) slots.push({ open: hhmm(s.open2), close: hhmm(s.close2) });
    return slots;
  }
  if (Array.isArray(restaurant.time_slots) && restaurant.time_slots.length > 0) {
    return restaurant.time_slots.map((s) => ({ open: hhmm(s.open), close: hhmm(s.close) }));
  }
  if (restaurant.opening_time && restaurant.closing_time) {
    return [{ open: hhmm(restaurant.opening_time), close: hhmm(restaurant.closing_time) }];
  }
  return null;
}

/** Créneaux d'aujourd'hui (voir slotsForDay). */
export const todaySlots = (restaurant) => slotsForDay(restaurant, 0);

/**
 * Créneaux de livraison de 30 min (départ toutes les 15 min) pour le jour
 * J+dayOffset. Aujourd'hui, seuls ceux qui commencent dans 30 min ou plus.
 */
export function deliveryWindows(restaurant, dayOffset = 0) {
  const ranges = slotsForDay(restaurant, dayOffset) ?? [{ open: '10:00', close: '22:00' }];
  const earliest = dayOffset === 0 ? mayotteNow().minutes + 30 : 0;
  const fmt = (min) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
  const windows = [];
  for (const { open, close } of ranges) {
    const o = toMin(open);
    let c = toMin(close);
    if (c <= o) c += 24 * 60; // créneau qui passe minuit
    for (let start = o; start + 30 <= c; start += 15) {
      if (start >= earliest) windows.push(`${fmt(start)} – ${fmt(start + 30)}`);
    }
  }
  return windows;
}

/** Horaires du jour à afficher (« 10:30 – 14:30 · 18:00 – 21:00 »). */
export function todayHoursLabel(restaurant) {
  const slots = todaySlots(restaurant);
  if (slots === null) return null;
  if (slots.length === 0) return "Fermé aujourd'hui";
  return slots.map((s) => `${s.open} – ${s.close}`).join(' · ');
}

/**
 * Calcule si un restaurant est ouvert en ce moment à Mayotte.
 * Respecte la fermeture forcée (is_open / is_open_manual à false).
 */
export function computeIsOpen(restaurant) {
  if (!restaurant) return false;
  if (restaurant.is_open_manual === false || restaurant.is_open === false) return false;
  const slots = todaySlots(restaurant);
  if (slots === null) return restaurant.is_open ?? true;
  const { minutes } = mayotteNow();
  return slots.some((s) => inSlot(minutes, s.open, s.close));
}
